import { ActionMeta } from "react-select5";
import { useController, useFormContext } from "react-hook-form";
import { ComponentProps, useMemo, useRef, useState } from "react";
import {filterOptionDefault, MultiCollapsible} from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { useUpdateEffect } from "@hooks";
import { InitialContact, SelfRegisterOption } from "@bonliva-crm/shared";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";

type Option = {
  label: string;
  value: string;
  parent?: string;
};

export type InitialContactFilterProps = FilterWithFavouriteProps;

export const InitialContactFilter = (props: InitialContactFilterProps) => {
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([
    `${InitialContact.SelfRegister}`
  ]);
  const title = props.label ?? locale.InitialContact;
  const options = useMemo<Option[]>(() => {
    const initialContactOptions: Option[] = [
      InitialContact.CreatedByBonliva,
      InitialContact.Recommendation,
      InitialContact.SelfRegister
    ].toLabelValue((v) => locale.initialContact[v]);

    const selfRegisterOptions = Object.values(SelfRegisterOption)
      .sortBy((v) => v !== SelfRegisterOption.SelfRegisterNotSpecified)
      .map((value) => ({
        value,
        label: locale.selfRegisterOption[value],
        parent: `${InitialContact.SelfRegister}`
      }));

    return [...initialContactOptions, ...selfRegisterOptions];
  }, []);

  const selectRef = useRef(null);
  const { setValue, resetField, register, watch } = useFormContext();
  const { fieldState } = useController({ name: props.name });
  register(props.name); // cant reset field without registering it
  const [expanded, setExpanded] = useState(fieldState.isDirty);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => {
    selectRef.current?.clearValue();
    resetField(props.name);
  };

  const value = watch(props.name) as string[];

  const onChange = (options: Option[], actionMeta: ActionMeta<Option>) => {
    const { action, option } = actionMeta;
    let finalValue = options;

    if (action === "select-option") {
      const parent = option.parent;
      if (option.value === `${parent}`) {
        finalValue = options.filter((x) => !x.value.startsWith(`${parent}`));
        finalValue.push(option);
      }
    }

    setValue(
      props.name,
      finalValue.map((option) => option.value),
      { shouldDirty: true }
    );
  };

  const onCollapse = (option: Option) => {
    const value = option.value;
    if (collapsedGroups.some((x) => x === value)) {
      setCollapsedGroups([...collapsedGroups.filter((x) => x !== value)]);
    } else {
      collapsedGroups.push(value);
      setCollapsedGroups([...collapsedGroups]);
    }
  };

  const isOptionSelected = (option: Option) => {
    if (!value) return false;
    const parent = option.parent;
    if (value?.some((v) => v === `${parent}`)) {
      return true;
    }

    return value?.some((v) => v === option.value);
  };

  const isOptionDisabled = (option: Option) => {
    const parent = option.parent;
    if (option.value === `${parent}`) {
      return false;
    }

    return value?.some((v) => v === `${parent}`);
  };

  const filterOption = (option: Option, inputValue?: string) => {
    if (inputValue) {
      return filterOptionDefault(option, inputValue);
    }

    const parent = option.parent;
    if (!parent) return true;

    if (option.value === `${parent}`) {
      return true;
    }

    return !collapsedGroups.some((x) => x === parent);
  };

  const isCollapsible = (option: Option) => option.value.endsWith("SelfRegister");

  const isCollapsed = (option: Option) => {
    const parent = option.parent;

    return collapsedGroups.some((x) => x === parent);
  };

  const isOptionStandAlone = (option: Option) => {
    return (
      option.parent === undefined && option.value !== `${InitialContact.SelfRegister}`
    );
  };

  const isShowCollapsibleOptionBadge = (option: LabelValue) => {
    const parent = option.value.split(":")[0];
    return value?.some((child) => child.includes(parent));
  };

  const commonProps: ComponentProps<typeof MultiCollapsible<Option>> = {
    selectRef,
    options,
    name: props.name,
    value:
      value?.map((c) => ({
        label: { ...locale.selfRegisterOption, ...locale.initialContact }[c],
        value: c
      })) ?? [],
    onChange,
    onCollapse,
    isCollapsible,
    isCollapsed,
    filterOption,
    isOptionSelected,
    isOptionDisabled,
    isOptionStandAlone,
    isShowCollapsibleOptionBadge,
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,

    maxMenuHeight: 300,
    isClearable: !!value,
    showBadge: !!value,
    onClear: () => resetField(props.name),
    closeMenuOnSelect: false,
    styleProps: {
      menu: {
        position: "absolute"
      }
    }
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiCollapsible<Option>
          {...commonProps}
          isClearable={false}
          openMenuOnFocus
          menuPortalTarget={document.body}
          controlShouldRenderValue
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiCollapsible<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
